import { DragulaContainer } from '@beesoft/react-dragula';
import type { Signal } from '@preact/signals-react';
import clsx from 'clsx';
import type { Key } from 'react';
import { useCallback } from 'react';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import { OverlayScrollDetectionType } from '../common/overlay-panel/overlay-panel.props';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import { SignalFilter } from '../signals/filter/signal-filter.component';
import DataGridColumn from './data-grid-column.component';
import type { DataGridColumnSettings, DataGridHeaderRowIndicator } from './data-grid-settings.interface';
import { reorderSignalColumns } from './functions/data-grid-signal-functions';

export interface DataGridHeaderProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  rowWidth: Signal<number>;
  indicatorRowWidth: Signal<number>;
  availableWidth: Signal<number>;
  search?: OdinDataRetrieval;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal: Signal<Array<ContinuousScrollerSort>>;
  scrollDetection?: OverlayScrollDetectionType;
  useIndicatorColumn: boolean;
  onColumnDragStart?: (column: DataGridColumnSettings) => void;
  onColumnDragEnd?: (column: DataGridColumnSettings) => void;
  headerRowIndicator?: DataGridHeaderRowIndicator;
  forceResetColumnMap: Map<Key, boolean>;
}

export default function DataGridHeader({
  columns,
  rowWidth,
  indicatorRowWidth,
  availableWidth,
  search,
  filterSignal,
  sortSignal,
  scrollDetection = OverlayScrollDetectionType.Parent,
  useIndicatorColumn,
  onColumnDragStart,
  onColumnDragEnd,
  headerRowIndicator,
  forceResetColumnMap,
}: DataGridHeaderProps) {
  const onItemsChanged = useCallback((items: Array<unknown>) => {
    const convertedColumns = items as Array<DataGridColumnSettings>;
    const columnIds = convertedColumns.map((column) => column.id);

    reorderSignalColumns(columns, columnIds);
  }, []);

  const onIndicatorColumnCreated = useCallback((element: HTMLElement) => {
    if (indicatorRowWidth.value !== element.offsetWidth) {
      indicatorRowWidth.value = element.offsetWidth;
    }
  }, []);

  const indicatorClassNames = clsx(
    'bg-mono-1 flex items-center justify-center w-10 h-full overflow-hidden',
    headerRowIndicator?.indicatorClassNames
  );

  const indicatorInnerClassNames = 'cursor-pointer';

  return columns.value ? (
    <div className="sticky top-0 z-10 grid min-w-full items-start">
      <div className="flex h-full bg-mono-1">
        {useIndicatorColumn &&
          (headerRowIndicator?.visible === true ? (
            <div
              ref={(element) => {
                if (element) onIndicatorColumnCreated(element);
              }}
              className={indicatorClassNames}
              onClick={headerRowIndicator.onIndicatorClicked}
            >
              <div className={indicatorInnerClassNames}>{headerRowIndicator.indicator}</div>
            </div>
          ) : (
            <div
              ref={(element) => {
                if (element) onIndicatorColumnCreated(element);
              }}
              className={indicatorClassNames}
            />
          ))}
        <SignalFilter value={columns} filter={(column) => column.visible}>
          {(filteredColumns) => (
            <DragulaContainer
              containerName="columns"
              items={filteredColumns}
              identityField="id"
              sortDirection="horizontal"
              onItemsChanged={onItemsChanged}
              className="flex w-full border-b border-gray-4"
            >
              {(item: unknown) => {
                const column = item as DataGridColumnSettings;

                return (
                  <DataGridColumn
                    columns={columns}
                    rowWidth={rowWidth}
                    indicatorRowWidth={indicatorRowWidth}
                    availableWidth={availableWidth}
                    key={column.id}
                    column={column}
                    scrollDetection={scrollDetection}
                    search={search}
                    filterSignal={filterSignal}
                    sortSignal={sortSignal}
                    onColumnDragStart={onColumnDragStart}
                    onColumnDragEnd={onColumnDragEnd}
                    forceResetColumnMap={forceResetColumnMap}
                  />
                );
              }}
            </DragulaContainer>
          )}
        </SignalFilter>
      </div>
    </div>
  ) : null;
}
